import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AlertController, ModalController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DateHelperService } from 'src/app/core/helpers/date-helper/date-helper.service';
import { Appointment } from 'src/app/core/models/appointment.model';
import { TimeEntry } from 'src/app/core/models/time-entry.model';
import { AppointmentService } from 'src/app/core/services/appointment/appointment.service';
import { SyncService } from 'src/app/core/sync/sync.service';
import { TimeEntryService } from 'src/app/core/time-entry/time-entry.service';
import { TimeTrackingFormComponent } from '../../time-tracking-form/time-tracking-form.component';

@Component({
	selector: 'app-time-tab',
	templateUrl: './time-tab.component.html',
	styleUrls: ['./time-tab.component.scss'],
})
export class TimeTabComponent {

	hiddenSections = [] as number[];
	appointment: Appointment;
	timeEntries: TimeEntry[];
	destroyed$: Subject<boolean>;

	constructor(
		private activatedRoute: ActivatedRoute,
		private appointmentService: AppointmentService,
		private modalController: ModalController,
		private dateHelperService: DateHelperService,
		private timeEntryService: TimeEntryService,
		private alertController: AlertController,
		private syncService: SyncService,
	) { }

	async ionViewDidEnter() {
		this.destroyed$ = new Subject();
		const id = this.activatedRoute.snapshot.parent.parent.params?.id;
		this.appointment = await this.appointmentService.getLocalAppointment(id);

		await this.getTimeCodes();

		this.updateAfterSync();
	}

	ionViewWillLeave() {
		this.destroyed$.next(true);
		this.destroyed$.complete();
	}

	updateAfterSync() {
		this.syncService.isSyncing$
			.pipe(takeUntil(this.destroyed$))
			.subscribe(async isSyncing => {
				if (!isSyncing) {
					await this.getTimeCodes();
				}
			});
	}

	async getTimeCodes() {
		this.timeEntries = (await this.timeEntryService.getAll())
			.find(entry => entry.apptId === this.appointment.id)
			?.entries;
	}

	getDisplayTime(min: number): string {
		const { hours, minutes } = this.dateHelperService.getHoursMinutesFromMinutes(min);

		return `${hours}h ${minutes}m`;
	}


	async onClickAddTimeCode(): Promise<void> {
		await this.showTimeCodeForm(null, 'Add');
	}

	async onClickEditTimeEntry(timeEntry: TimeEntry): Promise<void> {
		await this.showTimeCodeForm(timeEntry, 'Edit');
	}

	async showTimeCodeForm(timeEntry: TimeEntry, mode: string) {
		let apptTimeEntry = await this.timeEntryService.getAppointmentTimeEntry(this.appointment.id);
		if (!apptTimeEntry) {
			apptTimeEntry = await this.timeEntryService.newAppointmentTimeEntry(this.appointment);
		}
		const modal = await this.modalController.create({
			component: TimeTrackingFormComponent,
			componentProps: {
				mode,
				timeEntry,
				apptTimeEntry,
				hidden: ['appointment'],
			},
			cssClass: 'full-screen-modal',
		});

		await modal.present();
		await modal.onDidDismiss();
		await this.getTimeCodes();
	}

	async deleteTimeEntry(timeEntry: TimeEntry): Promise<void> {
		await this.timeEntryService.deleteTimeEntry(timeEntry, this.appointment.id, this.appointment.techId);
		await this.getTimeCodes();
	}

	async onClickDeleteTimeCode(timeEntry): Promise<void> {
		const alert = await this.alertController.create({
			header: 'Confirm Delete',
			message: 'Are you sure you would like to delete this time entry?',
			backdropDismiss: false,
			buttons: [
				{
					text: 'No',
					role: 'cancel',
					cssClass: 'secondary',
				}, {
					text: 'Yes',
					handler: async () => {
						await this.deleteTimeEntry(timeEntry);
					}
				}
			]
		});

		await alert.present();
	}

	onClickToggleSection(sectionIndex: number) {
		const foundIndex = this.hiddenSections.findIndex(index => index === sectionIndex);
		if (foundIndex === -1) {
			this.hiddenSections.push(sectionIndex);
			return;
		}

		this.hiddenSections.splice(foundIndex, 1);
	}

	isVisible(sectionIndex: number) {
		return (this.hiddenSections.findIndex(index => index === sectionIndex) > -1);
	}

}
